import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Users, Search, Filter, Eye } from "lucide-react";
import Layout from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { API } from "../../App";
import {
  TENANT_STAGE_LABELS, TENANT_STAGE_COLORS, formatINR, fmtDate, fmtDateTime,
  VISIT_STATUSES,
} from "./_rentalHelpers";

const empty = {
  name: "", phone: "", email: "", source: "",
  occupation: "", tenant_type: "family",
  budget_min: "", budget_max: "",
  configuration: "", furnishing_requirement: "",
  move_in_timeline: "", parking_required: false, lease_duration_months: "",
  stage: "new", notes: "",
};

const Form = ({ value, onChange, meta }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
      <div><Label>Name *</Label><Input data-testid="form-tenant-name" value={value.name} onChange={e => set("name", e.target.value)} /></div>
      <div><Label>Phone *</Label><Input data-testid="form-tenant-phone" value={value.phone} onChange={e => set("phone", e.target.value)} /></div>
      <div><Label>Email</Label><Input value={value.email} onChange={e => set("email", e.target.value)} /></div>
      <div><Label>Source</Label><Input placeholder="walk-in, 99acres, referral…" value={value.source} onChange={e => set("source", e.target.value)} /></div>
      <div><Label>Occupation</Label><Input value={value.occupation} onChange={e => set("occupation", e.target.value)} /></div>
      <div>
        <Label>Tenant type</Label>
        <Select value={value.tenant_type} onValueChange={v => set("tenant_type", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {(meta?.tenant_types || ["family", "bachelor", "corporate"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label>Budget min</Label><Input type="number" value={value.budget_min} onChange={e => set("budget_min", e.target.value)} /></div>
      <div><Label>Budget max</Label><Input type="number" value={value.budget_max} onChange={e => set("budget_max", e.target.value)} /></div>
      <div><Label>Configuration</Label><Input placeholder="2BHK / 3BHK" value={value.configuration} onChange={e => set("configuration", e.target.value)} /></div>
      <div><Label>Furnishing requirement</Label><Input value={value.furnishing_requirement} onChange={e => set("furnishing_requirement", e.target.value)} /></div>
      <div><Label>Move-in timeline</Label><Input placeholder="immediate / 30d / 60d" value={value.move_in_timeline} onChange={e => set("move_in_timeline", e.target.value)} /></div>
      <div><Label>Lease duration (months)</Label><Input type="number" value={value.lease_duration_months} onChange={e => set("lease_duration_months", e.target.value)} /></div>
      <div className="flex items-center gap-2 pt-7"><Switch checked={value.parking_required} onCheckedChange={v => set("parking_required", v)} /> <span className="text-sm">Parking required</span></div>
      <div>
        <Label>Stage</Label>
        <Select value={value.stage} onValueChange={v => set("stage", v)}>
          <SelectTrigger data-testid="form-tenant-stage"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(TENANT_STAGE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={value.notes} onChange={e => set("notes", e.target.value)} /></div>
    </div>
  );
};

export default function RentalTenantLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stageFilter !== "all") params.stage = stageFilter;
      if (search.trim()) params.search = search.trim();
      const [l, m] = await Promise.all([
        axios.get(`${API}/rentals/tenant-leads`, { params }),
        meta ? Promise.resolve({ data: meta }) : axios.get(`${API}/rentals/meta`),
      ]);
      setLeads(l.data || []);
      setMeta(m.data);
    } catch (e) {
      toast.error("Failed to load tenant leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [stageFilter]);
  const onSearchKey = (e) => { if (e.key === "Enter") load(); };

  const startCreate = () => { setEditing(null); setForm(empty); setShowCreate(true); };
  const startEdit = (t) => {
    setEditing(t);
    setForm({
      ...empty, ...t,
      budget_min: t.budget_min ?? "",
      budget_max: t.budget_max ?? "",
      lease_duration_months: t.lease_duration_months ?? "",
    });
    setShowCreate(true);
  };

  const submit = async () => {
    if (!form.name?.trim() || !form.phone?.trim()) {
      toast.error("Name and phone required");
      return;
    }
    const payload = {
      ...form,
      budget_min: form.budget_min === "" ? null : Number(form.budget_min),
      budget_max: form.budget_max === "" ? null : Number(form.budget_max),
      lease_duration_months: form.lease_duration_months === "" ? null : Number(form.lease_duration_months),
    };
    try {
      if (editing) {
        await axios.put(`${API}/rentals/tenant-leads/${editing.tenant_lead_id}`, payload);
        toast.success("Tenant lead updated");
      } else {
        await axios.post(`${API}/rentals/tenant-leads`, payload);
        toast.success("Tenant lead created");
      }
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const openDetail = async (t) => {
    try {
      const res = await axios.get(`${API}/rentals/tenant-leads/${t.tenant_lead_id}`);
      setDetail(res.data);
    } catch {
      toast.error("Failed to load details");
    }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="rental-tenant-leads-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-600" /> Tenant Leads
            </h1>
            <p className="text-sm text-slate-500">Inbound demand pipeline</p>
          </div>
          <Button onClick={startCreate} data-testid="create-tenant-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> New Tenant Lead
          </Button>
        </div>

        <Card className="p-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400" />
            <Input data-testid="tenant-search" placeholder="Search name / phone / email (Enter)" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={onSearchKey} />
          </div>
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-[200px]" data-testid="tenant-stage-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {Object.entries(TENANT_STAGE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Type / Config</th>
                  <th className="px-3 py-2 text-left">Budget</th>
                  <th className="px-3 py-2 text-left">Stage</th>
                  <th className="px-3 py-2 text-left">Updated</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Loading…</td></tr>}
                {!loading && leads.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No tenant leads yet.</td></tr>}
                {leads.map(t => (
                  <tr key={t.tenant_lead_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tenant-row-${t.tenant_lead_id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.phone}{t.email ? ` · ${t.email}` : ""}</p>
                    </td>
                    <td className="px-3 py-2 text-xs">{t.tenant_type || "—"} · {t.configuration || "—"}</td>
                    <td className="px-3 py-2 text-xs">{formatINR(t.budget_min)} – {formatINR(t.budget_max)}</td>
                    <td className="px-3 py-2">
                      <Badge className={TENANT_STAGE_COLORS[t.stage] || "bg-slate-100"}>{TENANT_STAGE_LABELS[t.stage] || t.stage}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(t.updated_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(t)} data-testid={`view-tenant-${t.tenant_lead_id}`}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(t)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Tenant Lead" : "New Tenant Lead"}</DialogTitle>
            </DialogHeader>
            <Form value={form} onChange={setForm} meta={meta} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-tenant" className="bg-emerald-600 hover:bg-emerald-700">
                {editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <SheetContent side="right" className="max-w-lg w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{detail?.name || ""}</SheetTitle>
            </SheetHeader>
            {detail && (
              <div className="mt-4 space-y-3 text-sm">
                <Badge className={TENANT_STAGE_COLORS[detail.stage] || "bg-slate-100"}>
                  {TENANT_STAGE_LABELS[detail.stage] || detail.stage}
                </Badge>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Phone:</span> {detail.phone}</div>
                  <div><span className="text-slate-500">Email:</span> {detail.email || "—"}</div>
                  <div><span className="text-slate-500">Type:</span> {detail.tenant_type || "—"}</div>
                  <div><span className="text-slate-500">Config:</span> {detail.configuration || "—"}</div>
                  <div><span className="text-slate-500">Budget:</span> {formatINR(detail.budget_min)} – {formatINR(detail.budget_max)}</div>
                  <div><span className="text-slate-500">Furnishing:</span> {detail.furnishing_requirement || "—"}</div>
                  <div><span className="text-slate-500">Move-in:</span> {detail.move_in_timeline || "—"}</div>
                  <div><span className="text-slate-500">Lease:</span> {detail.lease_duration_months || "—"} mo</div>
                </div>
                {detail.notes && <div className="bg-slate-50 p-2 rounded text-slate-700">{detail.notes}</div>}
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1 mt-3">Visits</p>
                  {(detail.visits || []).length === 0 ? <p className="text-xs text-slate-400 italic">No visits</p> : (
                    detail.visits.map(v => (
                      <div key={v.rental_visit_id} className="text-xs p-2 rounded border border-slate-100 bg-slate-50 mb-1">
                        {fmtDateTime(v.scheduled_at)} · {v.status} {v.feedback ? `· ${v.feedback.slice(0, 80)}` : ""}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
